import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaParagraph,
  FaTextWidth,
  FaExclamationTriangle
} from 'react-icons/fa';

// Import images
import paragraph from './assets/images/paragraph.png';
import paragraph2 from './assets/images/paragraph2.png';
import paragraph3 from './assets/images/paragraph3.png';

const HTMLLesson3 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<body>
  <p>This is it</p>
</body>`;

  const code2 = `<body>
  <p>This paragraph
  contains a lot of lines.
  But the browser ignores it because
  it doesn't really matter.</p>
</body>`;

  const code3 = `<body>
  <p>This is a paragraph
  <br />
</body>`;

  const sections = [
    {
      title: "HTML Paragraphs",
      content: "The HTML <p> element defines a paragraph and is one of the most commonly used elements in web development",
      keyPoints: [
        "Browsers automatically add white space (margin) before and after paragraphs",
        "Paragraphs are block-level elements that create natural content breaks"
      ],
      icon: <FaParagraph className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "HTML Display & Whitespace", 
      content: "Understanding how browsers handle whitespace and line breaks in HTML code",
      keyPoints: [
        "Browsers remove extra spaces and line breaks when displaying pages",
        "HTML output varies across different screen sizes and devices",
        "You cannot control layout with spaces in HTML code alone"
      ],
      icon: <FaTextWidth className="section-icon" />,
      color: "#1d4ed8",
      code: code2
    },
    {
      title: "Proper Tag Usage",
      content: "The importance of using proper HTML syntax and closing tags correctly",
      keyPoints: [
        "Most browsers will display HTML even with missing end tags, but don't rely on this",
        "Dropping end tags can produce unexpected results or errors",
        "Always practice proper HTML syntax for reliable results"
      ],
      icon: <FaExclamationTriangle className="section-icon" />,
      color: "#1e40af",
      code: code3
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 3 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Paragraphs And Text Formatting</h1>
              <p className="html-lesson-subtitle">
                Master paragraph elements, whitespace handling, and proper HTML syntax
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index)}
                className={`html-content-card ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content" dangerouslySetInnerHTML={{ __html: section.content }} />
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for each section */}
                <div className="html-section-code">
                  <div className="html-code-badge">
                    <FaCode className="html-badge-icon" />
                    Example
                  </div>
                  <div className="html-code-block-small">
                    <CopyBlock
                      language={language}
                      text={section.code}
                      showLineNumbers={lineNumbers}
                      theme={dracula}
                      wrapLines={true}
                      codeBlock
                    />
                  </div>
                </div>
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Visual Examples Section */}
          <div 
            className="html-visual-examples"
            ref={(el) => addSectionRef(el, sections.length)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaParagraph className="html-badge-icon" />
                Visual Examples
              </div>
              <h3 className="html-visual-title">
                See Paragraphs in Action
              </h3>
            </div>
            
            <div className="html-visual-grid">
              <div className="html-visual-item">
                <div className="html-visual-image">
                  <img src={paragraph} alt="Basic paragraph example" />
                </div>
                <div className="html-visual-content">
                  <h4>Basic Paragraph</h4>
                  <p>Simple paragraph element with automatic spacing</p>
                </div>
              </div>
              
              <div className="html-visual-item">
                <div className="html-visual-image">
                  <img src={paragraph2} alt="Whitespace handling example" />
                </div>
                <div className="html-visual-content">
                  <h4>Whitespace Handling</h4>
                  <p>How browsers collapse multiple spaces and line breaks</p>
                </div>
              </div>
              
              <div className="html-visual-item">
                <div className="html-visual-image">
                  <img src={paragraph3} alt="Line break example" />
                </div>
                <div className="html-visual-content">
                  <h4>Line Breaks</h4>
                  <p>Using &lt;br&gt; tags for manual line breaks</p>
                </div>
              </div>
            </div>
            
            <div className="html-visual-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Visual Examples Tutorial
              </a>
            </div>
          </div>

          {/* Comprehensive Code Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Putting It All Together
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={`<!DOCTYPE html>
<html>
<head>
    <title>Paragraph Examples</title>
</head>
<body>
    <!-- Basic Paragraph -->
    <p>This is a basic paragraph with automatic spacing.</p>
    
    <!-- Multiple Paragraphs -->
    <p>This is the first paragraph. Notice the spacing between paragraphs.</p>
    <p>This is the second paragraph. Browsers add margin automatically.</p>
    
    <!-- Paragraph with Line Breaks -->
    <p>This paragraph contains<br>a manual line break<br>using the br tag.</p>
    
    <!-- Whitespace Example -->
    <p>This     paragraph    has    extra    spaces    but    the    browser    collapses    them.</p>
    
    <!-- Always Close Your Tags -->
    <p>This paragraph is properly closed with an end tag.</p>
    <!-- Not like this: <p>This paragraph is missing its end tag -->
</body>
</html>`}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-action-header">
              <h3>Ready to format text like a pro?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned how to work with paragraphs, handle whitespace, and use proper HTML syntax. Practice creating well-structured content!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-line-break" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more practice? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson3;